/**
 * What an offer takes off a line, for display at the till.
 *
 * This deliberately mirrors OfferEngine.discountFor on the server, and the
 * duplication is the price of the register working offline — a quote endpoint
 * would be authoritative but useless the moment the shop's connection drops,
 * which is exactly when the till still has to work.
 *
 * The server recomputes every sale on submit and remains the authority, so a
 * disagreement here can only ever change what the cashier reads out, never
 * what the customer is charged. Keep the two in step.
 */
const round2 = (value) =>
  Math.round((Number(value) + Number.EPSILON) * 100) / 100;

const discountFor = (offer, quantity, unitPrice) => {
  const lineTotal = round2(unitPrice * quantity);

  switch (offer.offer_type) {
    case "percentage":
      return round2(
        (lineTotal * Math.min(Number(offer.discount_value) || 0, 100)) / 100,
      );

    case "fixed":
      // Off the line once, not off every unit
      return round2(Math.min(Number(offer.discount_value) || 0, lineTotal));

    case "buy_x_get_y": {
      const buy = parseInt(offer.buy_quantity, 10);
      const free = parseInt(offer.get_quantity, 10);

      if (!buy || !free || quantity < buy + free) return 0;

      return round2(Math.floor(quantity / (buy + free)) * free * unitPrice);
    }

    default:
      return 0;
  }
};

/**
 * The best single offer for a line. Offers never stack — the same rule the
 * server applies, for the same reason.
 */
export const bestOfferFor = (product, quantity) => {
  const candidates =
    product?.offer?.all || (product?.offer ? [product.offer] : []);

  if (candidates.length === 0) return null;

  const unitPrice = Number(product.unit_price);

  return candidates.reduce((winner, candidate) => {
    const amount = discountFor(candidate, quantity, unitPrice);

    if (!winner || amount > winner.amount) {
      return amount > 0 ? { ...candidate, amount } : winner;
    }

    return winner;
  }, null);
};

/** Total coming off a whole ticket, and which offers did it. */
export const offersForTicket = (lines) => {
  const applied = [];

  lines.forEach((line) => {
    const best = bestOfferFor(line.product, line.quantity);
    if (best) applied.push({ ...best, product_id: line.product.id });
  });

  return {
    applied,
    total: round2(applied.reduce((sum, entry) => sum + entry.amount, 0)),
  };
};
